import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import {
    Modal,
    ModalBodyFooter,
    ModalProcessing,
    Button,
    ErrorMessage
} from '../../../../components/custom-essentials';
import { SelectSingle, FormikControl, DatePicker, Check } from '../../../../components/form';
import { formatDateApi } from '../../../../components/functions';

// Portal detection
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isReadingPlex = hostname.includes('readingplex');

// Portal-aware contract types
const readingPlexContractTypes = [
    { value: 'Reading', label: 'Reading' },
    { value: 'Writing', label: 'Writing' },
    { value: '1:1', label: '1:1' }
];

const mathPlexContractTypes = [
    { value: 'Month to Month', label: 'Month to Month' },
    { value: 'Contract', label: 'Contract' },
    { value: 'Sessions', label: 'Sessions' },
    { value: 'Up Front', label: 'Up Front' },
    { value: 'Extra', label: 'Extra' }
];

const contractTypes = isReadingPlex ? readingPlexContractTypes : mathPlexContractTypes;

// Validation schema - validates that option objects have valid values
const validationSchema = Yup.object().shape({
    student: Yup.mixed()
        .test('student-selected', 'Please select a student', function(value) {
            return value && value.value && value.value !== -1 && value.value !== '-1';
        }),
    type: Yup.mixed()
        .test('type-selected', 'Please select a contract type', function(value) {
            return value && value.value && value.value !== -1 && value.value !== '-1';
        }),
    startDate: Yup.string().required('Start date is required'),
    hoursPerCycle: Yup.number().min(0).required('Hours per cycle is required'),
    paymentPerCycle: Yup.number().min(0).required('Payment per cycle is required')
});

function ContractModal(props) {
    const {
        show = true,
        mode = 'create',
        contract,
        students = [],
        onClose,
        createContract,
        updateContract,
        deleteContract
    } = props;

    const [deleteProcessing, setDeleteProcessing] = useState(false);

    // Build student options - SelectSingle expects { value, label } objects
    const studentOptions = React.useMemo(() => {
        const opts = [{ value: -1, label: 'Please select...' }];
        if (Array.isArray(students)) {
            students.forEach(s => {
                if (s && s.user_id && s.first_name && s.last_name) {
                    opts.push({
                        value: s.user_id,
                        label: `${s.first_name} ${s.last_name}`.trim()
                    });
                }
            });
        }
        return opts;
    }, [students]);

    // Get initial student option object
    const getInitialStudent = () => {
        if (!contract?.student) return studentOptions[0];
        const found = studentOptions.find(opt => opt.value === contract.student || String(opt.value) === String(contract.student));
        return found || studentOptions[0];
    };

    // Get initial type option object
    const getInitialType = () => {
        if (!contract?.type) return { value: -1, label: 'Please select...' };
        const found = contractTypes.find(t => t.value === contract.type);
        return found || { value: -1, label: 'Please select...' };
    };

    // Initial form values
    const initialValues = {
        student: getInitialStudent(),
        type: getInitialType(),
        startDate: contract?.start_date ? formatDateApi(contract.start_date) : formatDateApi(new Date()),
        endDate: contract?.end_date ? formatDateApi(contract.end_date) : '',
        hoursPerCycle: contract?.minutes_per_cycle ? Math.round(contract.minutes_per_cycle / 60) : 8,
        currentCycle: contract?.current_cycle || 1,
        hoursRemaining: contract?.minutes_remaining ? Math.round(contract.minutes_remaining / 60) : 8,
        paymentPerCycle: contract?.payment_per_cycle || 300,
        initialPayment: contract?.initial_payment || 0,
        notes: contract?.notes || '',
        active: contract ? parseInt(contract.active) === 1 : true,
        showWarnings: contract ? parseInt(contract.show_warnings) === 1 : true
    };

    const handleSubmit = async (values, formikHelpers) => {
        const { setSubmitting, setStatus } = formikHelpers;
        setStatus(null);

        // Extract values from option objects - SelectSingle passes full objects
        const studentId = values.student?.value;
        const contractType = values.type?.value;

        // Validate selections
        if (!studentId || studentId === -1 || studentId === '-1') {
            setStatus('Please select a student.');
            setSubmitting(false);
            return;
        }

        if (!contractType || contractType === -1 || contractType === '-1') {
            setStatus('Please select a contract type.');
            setSubmitting(false);
            return;
        }

        // Build payload - backend expects specific format
        // Send empty string for endDate if not provided (backend converts '' to SQL NULL)
        const payload = {
            student: String(studentId),
            type: String(contractType),
            startDate: values.startDate,
            endDate: values.endDate && values.endDate.trim() ? values.endDate.trim() : '',
            minutesPerCycle: Math.round(Number(values.hoursPerCycle) * 60),
            currentCycle: parseInt(values.currentCycle) || 1,
            minutesRemaining: Math.round(Number(values.hoursRemaining) * 60),
            paymentPerCycle: parseFloat(values.paymentPerCycle) || 0,
            initialPayment: parseFloat(values.initialPayment) || 0,
            notes: String(values.notes || '').trim().substring(0, 1000),
            showWarnings: values.showWarnings ? 1 : 0,
            active: values.active ? 1 : 0
        };

        try {
            const action = mode === 'edit' ? updateContract : createContract;
            const actionPayload = mode === 'edit' ? { ...payload, id: contract?.id } : payload;
            
            const response = await action(actionPayload);

            if (!response || response.status >= 400) {
                const errorMsg = response?.data?.message || 'Unable to save contract. Please try again.';
                setStatus(errorMsg);
                setSubmitting(false);
                return;
            }

            setSubmitting(false);
            // Silently refresh data and close modal without showing success message
            // No delay needed - backend transaction is committed by the time we get here
            if (onClose) {
                onClose(true);
            }
        } catch (error) {
            console.error('[CONTRACT MODAL] Error:', error);
            setStatus('An unexpected error occurred. Please try again.');
            setSubmitting(false);
        }
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: handleSubmit
    });

    const handleDelete = async () => {
        if (!contract?.id) return;
        
        setDeleteProcessing(true);
        try {
            const response = await deleteContract({ id: contract.id });
            if (!response || response.status >= 400) {
                setDeleteProcessing(false);
                return;
            }
            setDeleteProcessing(false);
            // Silently refresh data and close modal
            if (onClose) {
                onClose(true);
            }
        } catch (error) {
            console.error('[CONTRACT MODAL] Delete error:', error);
            setDeleteProcessing(false);
        }
    };

    const closeModal = (shouldRefresh = false) => {
        if (onClose) onClose(shouldRefresh);
    };

    if (!show) return null;

    const modalTitle = mode === 'edit' ? 'Edit Contract' : mode === 'delete' ? 'Delete Contract' : 'New Contract';

    // Delete confirmation
    if (mode === 'delete') {
        return (
            <Modal show={show} onHide={() => closeModal(false)} preventClickout={false} className="w-1/2">
                <Modal.Header><h2>{modalTitle}</h2></Modal.Header>
                <ModalBodyFooter>
                    <Modal.Body>
                        <h4 className="text-mpLRed mb-2">Delete this contract?</h4>
                        <p>This action cannot be undone.</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <div className="flex flex-row gap-x-2 flex-wrap items-center">
                            {deleteProcessing && <ModalProcessing />}
                            <Button color="mpLRed" onClick={() => closeModal(false)} disabled={deleteProcessing}>Cancel</Button>
                            <Button color="hol-mpLRed" onClick={handleDelete} disabled={deleteProcessing}>Delete</Button>
                        </div>
                    </Modal.Footer>
                </ModalBodyFooter>
            </Modal>
        );
    }

    // Form
    return (
        <Modal show={show} onHide={() => closeModal(false)} preventClickout={false} className="w-1/2">
            <Modal.Header><h2>{modalTitle}</h2></Modal.Header>
            <ModalBodyFooter>
                <Modal.Body>
                    <div className="grid grid-cols-1 gap-y-4">
                        <div>
                            <h4>Student</h4>
                            <SelectSingle
                                id="contract-student"
                                name="student"
                                value={formik.values.student}
                                options={studentOptions}
                                onChange={formik.handleChange}
                            />
                            {formik.errors.student && formik.touched.student && (
                                <ErrorMessage color="mpLRed">{formik.errors.student}</ErrorMessage>
                            )}
                        </div>

                        <div>
                            <h4>Contract Type</h4>
                            <SelectSingle
                                id="contract-type"
                                name="type"
                                value={formik.values.type}
                                options={[{ value: -1, label: 'Please select...' }, ...contractTypes]}
                                onChange={formik.handleChange}
                            />
                            {formik.errors.type && formik.touched.type && (
                                <ErrorMessage color="mpLRed">{formik.errors.type}</ErrorMessage>
                            )}
                        </div>

                        <div className="flex flex-row gap-x-4">
                            <div className="w-1/2">
                                <h4>Hours Per Cycle</h4>
                                <FormikControl
                                    id="contract-hoursPerCycle"
                                    name="hoursPerCycle"
                                    type="number"
                                    value={formik.values.hoursPerCycle}
                                    onChange={formik.handleChange}
                                />
                                {formik.errors.hoursPerCycle && formik.touched.hoursPerCycle && (
                                    <ErrorMessage color="mpLRed">{formik.errors.hoursPerCycle}</ErrorMessage>
                                )}
                            </div>
                            <div className="w-1/4">
                                <h4>Current Cycle</h4>
                                <FormikControl
                                    id="contract-currentCycle"
                                    name="currentCycle"
                                    type="number"
                                    value={formik.values.currentCycle}
                                    onChange={formik.handleChange}
                                />
                            </div>
                            <div className="w-1/4">
                                <h4>Hours Remaining</h4>
                                <FormikControl
                                    id="contract-hoursRemaining"
                                    name="hoursRemaining"
                                    type="number"
                                    value={formik.values.hoursRemaining}
                                    onChange={formik.handleChange}
                                />
                            </div>
                        </div>

                        <div className="flex flex-row gap-x-4">
                            <div className="w-1/2">
                                <h4>Contract Start</h4>
                                <DatePicker
                                    id="contract-startDate"
                                    name="startDate"
                                    value={formik.values.startDate}
                                    onChange={formik.handleChange}
                                />
                                {formik.errors.startDate && formik.touched.startDate && (
                                    <ErrorMessage color="mpLRed">{formik.errors.startDate}</ErrorMessage>
                                )}
                            </div>
                            <div className="w-1/2">
                                <h4>Contract End</h4>
                                <DatePicker
                                    id="contract-endDate"
                                    name="endDate"
                                    value={formik.values.endDate}
                                    onChange={formik.handleChange}
                                />
                            </div>
                        </div>

                        <div className="flex flex-row gap-x-4">
                            <div className="w-1/2">
                                <h4>Initial Payment</h4>
                                <FormikControl
                                    id="contract-initialPayment"
                                    name="initialPayment"
                                    type="number"
                                    value={formik.values.initialPayment}
                                    onChange={formik.handleChange}
                                />
                            </div>
                            <div className="w-1/2">
                                <h4 className="text-mpLBlue">Payment Per Cycle</h4>
                                <FormikControl
                                    id="contract-paymentPerCycle"
                                    name="paymentPerCycle"
                                    type="number"
                                    value={formik.values.paymentPerCycle}
                                    onChange={formik.handleChange}
                                />
                                {formik.errors.paymentPerCycle && formik.touched.paymentPerCycle && (
                                    <ErrorMessage color="mpLRed">{formik.errors.paymentPerCycle}</ErrorMessage>
                                )}
                            </div>
                        </div>

                        <div>
                            <h4>Notes</h4>
                            <FormikControl
                                id="contract-notes"
                                name="notes"
                                as="textarea"
                                value={formik.values.notes}
                                onChange={formik.handleChange}
                                placeholder="Max. 1000 characters"
                            />
                        </div>

                        <div className="flex flex-row gap-x-4">
                            <Check
                                id="contract-active"
                                name="active"
                                color="mpTeal"
                                label="Contract Active"
                                checked={formik.values.active}
                                onChange={formik.handleChange}
                            />
                            <Check
                                id="contract-showWarnings"
                                name="showWarnings"
                                color="mpLBlue"
                                label="Show Warnings"
                                checked={formik.values.showWarnings}
                                onChange={formik.handleChange}
                            />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <div className="flex flex-row gap-x-2 flex-wrap items-center">
                        {formik.isSubmitting && <ModalProcessing />}
                        {formik.status && <ErrorMessage color="mpLRed">{formik.status}</ErrorMessage>}
                        <Button color="mpLRed" onClick={() => closeModal(false)} disabled={formik.isSubmitting}>Cancel</Button>
                        <Button color="mpBlue" onClick={formik.handleSubmit} disabled={formik.isSubmitting}>
                            {mode === 'edit' ? 'Save Changes' : 'Add Contract'}
                        </Button>
                    </div>
                </Modal.Footer>
            </ModalBodyFooter>
        </Modal>
    );
}

export default ContractModal;
